import React, { useState, useEffect } from 'react';
import api from '../api';

const emptyForm = { date: '', bank_statement_balance: '', ledger_balance: '' };

const Reconciliation = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/reconciliations/')
      .then(res => setRows(Array.isArray(res.data) ? res.data : res.data.results || []))
      .catch(() => setError('Failed to load reconciliations'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        date: form.date,
        bank_statement_balance: Number(form.bank_statement_balance),
      };
      if (form.ledger_balance !== '') {
        payload.ledger_balance = Number(form.ledger_balance);
      }
      await api.post('/reconciliations/', payload);
      setShowModal(false);
      setForm(emptyForm);
      setMessage('Reconciliation created');
      load();
    } catch (err) {
      const data = err.response?.data;
      const detail =
        data?.error
        || data?.detail
        || (data && typeof data === 'object'
          ? Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')
          : null)
        || 'Failed to create reconciliation';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const matchRow = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/reconciliations/${id}/match/`);
      setMessage(`Reconciliation #${id} marked matched`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Match failed');
    } finally {
      setBusyId(null);
    }
  };

  const adjustRow = async (id) => {
    setBusyId(id);
    setError('');
    try {
      const res = await api.post(`/reconciliations/${id}/post_adjustment/`);
      setMessage(`Adjustment JE #${res.data.journal_entry_id} posted`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Adjustment failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex-between">
        <h2>Reconciliation</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>New Reconciliation</button>
      </div>
      {error && <div className="error">{error}</div>}
      {message && <div style={{ color: '#1a7a42', marginBottom: 12 }}>{message}</div>}
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Bank Balance</th>
            <th>Ledger Balance</th>
            <th>Difference</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td>{r.date}</td>
              <td>{Number(r.bank_statement_balance).toLocaleString()}</td>
              <td>{Number(r.ledger_balance).toLocaleString()}</td>
              <td>{Number(r.difference).toLocaleString()}</td>
              <td><span className={`badge ${r.status}`}>{r.status}</span></td>
              <td>
                <button className="btn btn-outline" disabled={busyId === r.id} onClick={() => matchRow(r.id)} style={{ marginRight: 6 }}>
                  Match
                </button>
                <button className="btn btn-outline" disabled={busyId === r.id || Number(r.difference) === 0} onClick={() => adjustRow(r.id)}>
                  Post Adj JE
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create Reconciliation</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Bank Statement Balance</label>
                <input type="number" step="0.01" value={form.bank_statement_balance} onChange={e => setForm({ ...form, bank_statement_balance: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Ledger Balance (optional — defaults to Cash+Bank net)</label>
                <input type="number" step="0.01" value={form.ledger_balance} onChange={e => setForm({ ...form, ledger_balance: e.target.value })} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reconciliation;
